import type { Definition } from '../api/missions';

/*
 * The recipe's parameters a regeneration changes (release D, D11.14): what a
 * mission has now, as the platform's RegenerateParams names them, and which of
 * them a person changed. Kept apart from the component so the designer's
 * tests read them directly.
 */

/** The parameters a regeneration changes (the platform's RegenerateParams.NAMES). */
export interface RecipeParamValues {
  hour?: number;
  date?: string;
  players: number;
  squadSize: number;
  insertion: string;
  extraction: string;
  supports: Record<string, number>;
  startFrom: 'land' | 'sea';
  enemyStrength: string;
}

/** Where the night starts, as the definition says it: a ship's deck is the sea. */
export function startOf(def: Definition): 'land' | 'sea' {
  return def.points?.base?.kind === 'ship' ? 'sea' : 'land';
}

/** The values a mission was generated with, as the parameters name them. */
export function currentParams(def: Definition): RecipeParamValues {
  return {
    hour: def.time?.hour,
    date: def.time?.date,
    players: def.sides.players.slots,
    squadSize: def.sides.players.squadSize ?? 8,
    insertion: def.points?.insertion?.means ?? 'foot',
    extraction: def.points?.extraction?.means ?? 'foot',
    supports: { ...(def.supports ?? {}) },
    startFrom: startOf(def),
    enemyStrength: def.sides.enemy.strength ?? 'medium',
  };
}

/**
 * The parameters that differ from what the mission has: only these are sent,
 * so a regeneration changes nothing it was not asked to. Supports compare as
 * counts, a missing key being none.
 */
export function changedParams(
  from: RecipeParamValues,
  to: RecipeParamValues,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  (Object.keys(to) as (keyof RecipeParamValues)[]).forEach((k) => {
    if (k === 'supports') {
      const keys = new Set([...Object.keys(from.supports), ...Object.keys(to.supports)]);
      const differs = [...keys].some((s) => (from.supports[s] ?? 0) !== (to.supports[s] ?? 0));
      if (differs) out.supports = to.supports;
    } else if (to[k] !== undefined && to[k] !== from[k]) {
      out[k] = to[k];
    }
  });
  return out;
}
